import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { SEOHead } from '@/components/seo/SEOHead';
import { ArticleJsonLd, FAQJsonLd, BreadcrumbJsonLd } from '@/components/seo';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Clock, User, Linkedin, Facebook, Twitter, Mail, Link2, Home, Newspaper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePageDesign } from '@/hooks/usePageDesign';
import { sanitizeHtml } from '@/lib/sanitize';
import NotFound from './NotFound';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: businessSettings } = useQuery({
    queryKey: ['business-settings-blog-schema'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('company_name, logo_url, website_url')
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const { data: blogAuthor } = useQuery({
    queryKey: ['blog-author-default'],
    queryFn: async () => {
      const { data } = await pmsFrom('blog_authors')
        .select('*')
        .eq('is_default', true)
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  // Page design config — must be called before any early returns (React hooks rule)
  const { heroStyle, showBreadcrumbs, showLeadForm, isLoading: designLoading } = usePageDesign('blog');

  if (isLoading || designLoading) {
    return (
      <div className="container mx-auto px-4 py-20 max-w-4xl">
        <Skeleton className="h-10 w-3/4 mb-4" />
        <Skeleton className="h-6 w-1/2 mb-8" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!post) return <NotFound />;

  const buyerStageLabels: Record<string, string> = {
    awareness: 'Awareness',
    consideration: 'Consideration',
    decision: 'Decision',
    retention: 'Retention',
    advocacy: 'Advocacy',
  };

  const baseUrl = businessSettings?.website_url || window.location.origin;
  const postUrl = `${baseUrl}/blog/${post.slug}`;
  const encodedUrl = encodeURIComponent(postUrl);
  const encodedTitle = encodeURIComponent(post.meta_title || post.title);

  // Parse FAQ from content or faq field
  const faqData: Array<{ question: string; answer: string }> = [];
  if (post.faq && Array.isArray(post.faq)) {
    (post.faq as Array<{ question: string; answer: string }>).forEach(f => {
      if (f.question && f.answer) faqData.push(f);
    });
  }

  const authorFullName = blogAuthor
    ? `${blogAuthor.first_name} ${blogAuthor.last_name}`
    : post.author || undefined;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(postUrl);
  };

  return (
    <>
      <SEOHead
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt || ''}
      />

      <ArticleJsonLd
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt || ''}
        url={postUrl}
        imageUrl={post.featured_image || undefined}
        authorName={authorFullName}
        publishedAt={post.published_at || undefined}
        updatedAt={post.updated_at || undefined}
        wordCount={post.word_count || undefined}
        category={post.category || undefined}
        publisherName={businessSettings?.company_name || undefined}
        publisherLogoUrl={businessSettings?.logo_url || undefined}
      />

      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: baseUrl },
          { name: 'Blog', url: `${baseUrl}/resources` },
          { name: post.title, url: postUrl },
        ]}
      />

      {faqData.length > 0 && <FAQJsonLd faqs={faqData} />}

      <ConfigurableHero
        heroStyle={heroStyle}
        title={post.title}
        subtitle={post.excerpt || undefined}
        badge={post.category || undefined}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="h-4 w-4" /> },
          { label: 'Blog', href: '/resources', icon: <Newspaper className="h-4 w-4" /> },
          { label: post.title },
        ]}
        showBreadcrumbs={showBreadcrumbs}
        showTrustBadges={false}
        showCtaButtons={false}
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/resources" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <div className="flex flex-wrap items-center gap-3 mt-4 mb-10 text-sm text-muted-foreground">
          {authorFullName && (
            <span className="flex items-center gap-1.5 font-medium text-foreground/70"><User className="w-4 h-4 text-primary" /> {authorFullName}</span>
          )}
          {post.published_at && (
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          )}
          {post.read_time && (
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.read_time}</span>
          )}
          {post.category && <Badge variant="secondary" className="text-xs">{post.category}</Badge>}
          {post.buyer_stage && (
            <Badge variant="outline" className="text-xs">{buyerStageLabels[post.buyer_stage] || post.buyer_stage}</Badge>
          )}
        </div>

        {post.featured_image && (
          <img
            src={post.featured_image}
            alt={post.featured_image_alt || post.title}
            className="w-full rounded-xl mb-10 object-cover max-h-[480px] shadow-lg"
          />
        )}

        {post.content && (
          <article
            className="seo-content mx-auto"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
          />
        )}

        {/* Social Sharing */}
        <div className="mt-12 pt-8 border-t border-border">
          <p className="text-sm font-medium text-muted-foreground mb-3">Share this article</p>
          <div className="flex flex-wrap gap-2">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(221,44%,41%)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Facebook className="h-4 w-4" /> Facebook
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Twitter className="h-4 w-4" /> X / Twitter
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(210,80%,38%)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Linkedin className="h-4 w-4" /> LinkedIn
            </a>
            <a
              href={`mailto:?subject=${encodedTitle}&body=Check out this article: ${encodedUrl}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors"
            >
              <Mail className="h-4 w-4" /> Email
            </a>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleCopyLink}
            >
              <Link2 className="h-4 w-4" /> Copy Link
            </Button>
          </div>
        </div>

        {/* Author Card */}
        {blogAuthor && (
          <div className="mt-10 p-6 rounded-2xl bg-muted/40 border border-border">
            <div className="flex items-start gap-5">
              {blogAuthor.headshot_url ? (
                <img
                  src={blogAuthor.headshot_url}
                  alt={`${blogAuthor.first_name} ${blogAuthor.last_name}`}
                  className="w-20 h-20 rounded-full object-cover border-2 border-primary/20 flex-shrink-0"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="h-8 w-8 text-primary" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading text-lg font-semibold">
                    {blogAuthor.first_name} {blogAuthor.last_name}
                  </h3>
                  {blogAuthor.linkedin_url && (
                    <a
                      href={blogAuthor.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                    </a>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Author</p>
                {blogAuthor.bio && (
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{blogAuthor.bio}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlogPostPage;